import { createContext, useContext, useEffect, useMemo, useCallback, useState } from "react";
import { useAuth } from "@/context/AuthContext";

const I18nContext = createContext(null);

const RTL_LANGS = new Set(["he", "ar"]);

function detectInitial() {
  // Try saved preference, then browser
  const saved = localStorage.getItem("te_lang");
  if (saved && ["en", "he", "ar"].includes(saved)) return saved;
  const browser = (navigator.language || "en").slice(0, 2).toLowerCase();
  if (browser === "he" || browser === "iw") return "he";
  if (browser === "ar") return "ar";
  return "en";
}

export function I18nProvider({ children }) {
  const { user, refreshMe } = useAuth() || {};
  const [language, setLanguageState] = useState(detectInitial());

  // Sync to logged-in user's language preference (one-way: user.language → state)
  useEffect(() => {
    const target = user?.language;
    if (target && target !== language) {
      // safe outside-of-render scheduling via microtask to satisfy lint rule
      Promise.resolve().then(() => setLanguageState(target));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.language]);

  // Apply <html lang dir> globally
  useEffect(() => {
    const html = document.documentElement;
    html.setAttribute("lang", language);
    html.setAttribute("dir", RTL_LANGS.has(language) ? "rtl" : "ltr");
  }, [language]);

  const setLanguage = useCallback(async (lang) => {
    if (!["en", "he", "ar"].includes(lang)) return;
    setLanguageState(lang);
    localStorage.setItem("te_lang", lang);
    // Persist on user if logged in
    if (user) {
      try {
        const api = (await import("@/lib/api")).default;
        await api.patch("/users/me", { language: lang });
        if (refreshMe) await refreshMe();
      } catch (e) { /* silent */ }
    }
  }, [user, refreshMe]);

  const value = useMemo(() => ({
    language,
    setLanguage,
    isRTL: RTL_LANGS.has(language),
  }), [language, setLanguage]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export const useI18n = () => useContext(I18nContext) || { language: "en", setLanguage: () => {}, isRTL: false };
